import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useWS, useWSConnected } from '../hooks/useWebSocket';
import { formatTime } from '../utils/dateFormat';
import {
  buildTriage,
  deliverySummary,
  firmwareSummary,
  formatUptime,
  signalQuality,
  triageSummary,
} from '../utils/dashboardTriage';

// Events that should refresh the operations dashboard in real time.
const LIVE_EVENTS = [
  'device_status_change', 'telemetry_update', 'new_alert',
  'alert_acknowledged', 'alert_resolved',
  'device:vacation_mode_changed', 'device:config_applied',
  'checkin:slot_completed', 'checkin:slot_missed', 'button_press',
];

const titleCase = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '0%');

// Card tone. `idle` is the important one: a metric sitting at a healthy zero
// (no pending alerts, no missed check-ins) renders grey and recedes, instead of
// claiming the same red urgency as a real problem.
const TONES = {
  ok: { ring: 'ring-secondary-200/70', value: 'text-secondary-900', icon: 'bg-primary-50 text-primary-600' },
  idle: { ring: 'ring-secondary-200/70', value: 'text-secondary-300', icon: 'bg-secondary-100 text-secondary-400' },
  warn: { ring: 'ring-amber-300', value: 'text-amber-700', icon: 'bg-amber-50 text-amber-600' },
  bad: { ring: 'ring-red-300', value: 'text-red-700', icon: 'bg-red-50 text-red-600' },
};

const SEVERITY = {
  critical: {
    dot: 'bg-red-500', text: 'text-red-700', row: 'hover:bg-red-50/60',
    chip: 'bg-red-100 text-red-700', label: 'Critical',
  },
  warning: {
    dot: 'bg-amber-500', text: 'text-amber-700', row: 'hover:bg-amber-50/60',
    chip: 'bg-amber-100 text-amber-700', label: 'Warning',
  },
  info: {
    dot: 'bg-blue-400', text: 'text-blue-700', row: 'hover:bg-blue-50/60',
    chip: 'bg-blue-100 text-blue-700', label: 'Info',
  },
};

/** Headline metric. `bar` folds a breakdown inline so it needs no second panel. */
function StatCard({ label, value, sub, tone = 'ok', icon, to, bar }) {
  const t = TONES[tone] || TONES.ok;
  const inner = (
    <>
      <div className="flex items-center gap-2.5 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${t.icon}`}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
          </svg>
        </div>
        <span className="text-sm font-medium text-secondary-600">{label}</span>
      </div>
      <p className={`text-3xl font-bold tracking-tight ${t.value}`}>{value}</p>
      {sub && <p className="text-xs text-secondary-500 mt-1">{sub}</p>}
      {bar}
    </>
  );

  const cls = `block bg-white rounded-xl shadow-card ring-1 ${t.ring} p-4 sm:p-5 transition
    hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-primary-400`;

  return to
    ? <Link to={to} className={cls} aria-label={`${label}: ${value}`}>{inner}</Link>
    : <div className={cls}>{inner}</div>;
}

/** Proportional bar + legend. Zero-value segments are dropped entirely. */
function SegmentBar({ segments, className = 'mt-3' }) {
  const shown = segments.filter((s) => s.value > 0);
  const total = shown.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return null;
  return (
    <div className={className}>
      <div className="flex h-1.5 rounded-full overflow-hidden bg-secondary-100">
        {shown.map((s) => (
          <div
            key={s.label}
            className={s.fill}
            style={{ width: `${(s.value / total) * 100}%` }}
            title={`${s.label}: ${s.value}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-2">
        {shown.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1 text-[11px] text-secondary-500">
            <span className={`w-1.5 h-1.5 rounded-full ${s.fill}`} aria-hidden="true" />
            {s.label} {s.value}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The ranked "what is wrong right now" list. */
function AttentionBand({ items }) {
  if (items.length === 0) {
    return (
      <div className="flex items-center gap-3 bg-white rounded-xl shadow-card ring-1 ring-primary-200 p-4">
        <span className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </span>
        <div>
          <p className="text-sm font-semibold text-secondary-900">All clear</p>
          <p className="text-xs text-secondary-500">
            Every device is reporting, no alerts are open, and nothing failed to deliver.
          </p>
        </div>
      </div>
    );
  }

  const counts = triageSummary(items);

  return (
    <section
      aria-labelledby="needs-attention"
      className="bg-white rounded-xl shadow-card ring-1 ring-secondary-200/70 overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-secondary-100">
        <h2 id="needs-attention" className="text-sm font-semibold text-secondary-900">
          Needs attention
        </h2>
        <div className="flex items-center gap-1.5">
          {['critical', 'warning', 'info'].map((sev) => (counts[sev] > 0 ? (
            <span
              key={sev}
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${SEVERITY[sev].chip}`}
            >
              {counts[sev]} {SEVERITY[sev].label.toLowerCase()}
            </span>
          ) : null))}
        </div>
      </div>

      <ul className="divide-y divide-secondary-100">
        {items.map((item) => {
          const s = SEVERITY[item.severity];
          return (
            <li key={item.id}>
              <Link
                to={item.to}
                className={`flex items-start gap-3 px-4 sm:px-5 py-3 transition group ${s.row}
                  focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-400`}
              >
                <span
                  className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.dot}`}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-semibold ${s.text}`}>
                    <span className="sr-only">{s.label}: </span>{item.title}
                  </span>
                  <span className="block text-xs text-secondary-500 mt-0.5">{item.detail}</span>
                </span>
                <span className="hidden sm:flex items-center gap-1 text-xs font-medium text-secondary-400
                  group-hover:text-primary-600 transition shrink-0 mt-0.5">
                  {item.cta}
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Panel({ title, to, linkLabel = 'View', children }) {
  return (
    <div className="bg-white rounded-xl shadow-card ring-1 ring-secondary-200/70 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-secondary-900 text-sm">{title}</h2>
        {to && (
          <Link to={to} className="text-xs font-medium text-primary-600 hover:text-primary-700">
            {linkLabel}
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Label/value pair. A zero renders muted regardless of the requested colour —
 * colour on this page means "there is something here", not "this is a red
 * category".
 */
function Metric({ label, value, color, hint }) {
  const isZero = value === 0 || value === '0' || value === '--' || value == null;
  const colorMap = {
    red: 'text-red-600', amber: 'text-amber-600', blue: 'text-blue-600',
    green: 'text-emerald-600', secondary: 'text-secondary-900',
  };
  return (
    <div>
      <p className="text-xs text-secondary-500">{label}</p>
      <p className={`text-xl font-bold ${isZero ? 'text-secondary-300' : (colorMap[color] || 'text-secondary-900')}`}>
        {value == null ? '--' : value}
      </p>
      {hint && <p className="text-[11px] text-secondary-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function EmptyNote({ children }) {
  return <p className="text-xs text-secondary-400">{children}</p>;
}

/**
 * Presentational dashboard. Split from the data fetching below so the whole
 * page can be rendered from a fixture — in a test or a design preview —
 * without standing up the API.
 */
export function DashboardView({ stats, updatedAt = null, wsConnected = false }) {
  const { devices, alerts, checkins, ota, notifications, telemetry, subscriptions, patients } = stats;
  const wp = checkins.windowPosition || { positioned: 0, front: 0, middle: 0, back: 0 };
  const sev = alerts.bySeverity || {};

  const triage = buildTriage(stats);
  const fw = firmwareSummary(devices.firmwareDistribution);
  const delivery = deliverySummary(notifications);

  const liveFleet = Math.max(0, (devices.total || 0) - (devices.decommissioned || 0));
  const openAlerts = (sev.critical || 0) + (sev.high || 0) + (sev.medium || 0) + (sev.low || 0);
  const scheduledToday = (checkins.completedToday || 0) + (checkins.missedToday || 0);

  // Rollout progress across the 30-day window.
  const otaQueued = (ota.pending || 0) + (ota.notified || 0);
  const otaMoving = (ota.downloading || 0) + (ota.applying || 0);
  const otaTotal = otaQueued + otaMoving + (ota.success || 0) + (ota.failed || 0) + (ota.cancelled || 0);

  const sig = signalQuality(telemetry.avgSignal);

  // Headline tones — driven by the state, not by a fixed per-card colour.
  const deviceTone = devices.offline === 0
    ? 'ok'
    : (liveFleet > 0 && devices.offline / liveFleet >= 0.5 ? 'bad' : 'warn');
  const alertTone = alerts.criticalPending > 0 ? 'bad' : (openAlerts > 0 ? 'warn' : 'idle');
  const checkinTone = checkins.missedToday > 0 ? 'bad' : (scheduledToday > 0 ? 'ok' : 'idle');

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">Operations Dashboard</h1>
          <p className="text-secondary-500 text-sm mt-1">Live overview of the CheckInCare fleet</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-secondary-500">
          <span
            className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-500 animate-pulse' : 'bg-secondary-300'}`}
            aria-hidden="true"
          />
          <span>{wsConnected ? 'Live' : 'Reconnecting'}</span>
          {updatedAt && <span className="text-secondary-400">· updated {formatTime(updatedAt)}</span>}
        </div>
      </div>

      <AttentionBand items={triage} />

      {/* Headline stats. Each folds in the breakdown that used to sit in a
          separate panel below, so a number and its composition read together. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          to="/devices"
          label="Devices reporting"
          value={`${devices.online}/${liveFleet}`}
          sub={devices.offline > 0
            ? `${devices.offline} offline · no heartbeat in 15 min`
            : 'All devices reporting'}
          tone={deviceTone}
          icon="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
          bar={(
            <>
              {/* Online/offline only: those two partition the live fleet exactly.
                  Vacation and maintenance overlap them — a device on vacation is
                  still online — so folding them in would make the bar sum past
                  the fleet size and render meaningless proportions. */}
              <SegmentBar
                segments={[
                  { label: 'Online', value: devices.online || 0, fill: 'bg-emerald-500' },
                  { label: 'Offline', value: devices.offline || 0, fill: 'bg-red-500' },
                ]}
              />
              {(devices.onVacation > 0 || devices.maintenance > 0) && (
                <p className="text-[11px] text-secondary-400 mt-1.5">
                  {[
                    devices.onVacation > 0 && `${devices.onVacation} on vacation`,
                    devices.maintenance > 0 && `${devices.maintenance} in maintenance`,
                  ].filter(Boolean).join(' · ')}
                </p>
              )}
            </>
          )}
        />

        <StatCard
          to="/alerts"
          label="Open alerts"
          value={openAlerts}
          sub={openAlerts > 0
            ? `${alerts.criticalPending} critical unacknowledged · ${alerts.resolvedToday} resolved today`
            : `Nothing open · ${alerts.resolvedToday} resolved today`}
          tone={alertTone}
          icon="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          bar={(
            <SegmentBar
              segments={[
                { label: 'Critical', value: sev.critical || 0, fill: 'bg-red-500' },
                { label: 'High', value: sev.high || 0, fill: 'bg-amber-500' },
                { label: 'Medium', value: sev.medium || 0, fill: 'bg-blue-400' },
                { label: 'Low', value: sev.low || 0, fill: 'bg-secondary-400' },
              ]}
            />
          )}
        />

        <StatCard
          to="/monitor"
          label="Check-ins today"
          value={checkins.complianceRate != null ? `${checkins.complianceRate}%` : 'None yet'}
          sub={scheduledToday > 0
            ? `${checkins.completedToday} done · ${checkins.missedToday} missed of ${scheduledToday}`
            : 'No scheduled window has closed yet today'}
          tone={checkinTone}
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          bar={(
            <SegmentBar
              segments={[
                { label: 'Completed', value: checkins.completedToday || 0, fill: 'bg-emerald-500' },
                { label: 'Missed', value: checkins.missedToday || 0, fill: 'bg-red-500' },
              ]}
            />
          )}
        />

        <StatCard
          to="/patients"
          label="Patients"
          value={patients?.total ?? 0}
          sub={subscriptions.total > 0
            ? `${subscriptions.total} active subscription${subscriptions.total === 1 ? '' : 's'}`
            : 'No active subscriptions'}
          tone={(patients?.total ?? 0) > 0 ? 'ok' : 'idle'}
          icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          bar={(
            <SegmentBar
              segments={[
                { label: 'Basic', value: subscriptions.basic || 0, fill: 'bg-secondary-400' },
                { label: 'Premium', value: subscriptions.premium || 0, fill: 'bg-blue-400' },
                { label: 'Pro', value: subscriptions.pro || 0, fill: 'bg-emerald-500' },
              ]}
            />
          )}
        />
      </div>

      {/* Operational detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        <Panel title="Connection health (24h)" to="/devices" linkLabel="Devices">
          {telemetry.samples > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                <Metric
                  label="Avg signal"
                  value={telemetry.avgSignal != null ? `${telemetry.avgSignal} dBm` : '--'}
                  color="secondary"
                  hint={sig ? sig.label : null}
                />
                <Metric
                  label="Weakest"
                  value={telemetry.minSignal != null ? `${telemetry.minSignal} dBm` : '--'}
                  color={telemetry.minSignal != null && telemetry.minSignal <= -80 ? 'amber' : 'secondary'}
                />
                <Metric
                  label="Avg uptime"
                  value={formatUptime(telemetry.avgUptimeHours)}
                  color={telemetry.avgUptimeHours != null && telemetry.avgUptimeHours < 1 ? 'red' : 'secondary'}
                  hint={telemetry.avgUptimeHours != null && telemetry.avgUptimeHours < 1 ? 'Rebooting' : null}
                />
              </div>
              <p className="text-[11px] text-secondary-400 mt-3 pt-3 border-t border-secondary-100">
                {telemetry.samples} telemetry {telemetry.samples === 1 ? 'sample' : 'samples'} in the last 24 hours
              </p>
            </>
          ) : (
            <EmptyNote>No telemetry in the last 24 hours.</EmptyNote>
          )}
        </Panel>

        <Panel title="Notification delivery (7d)" to="/notifications" linkLabel="Log">
          {delivery.total > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                <Metric
                  label="Delivered"
                  value={delivery.rate != null ? `${delivery.rate}%` : '--'}
                  color={delivery.rate != null && delivery.rate < 100 ? 'amber' : 'green'}
                  hint={`${delivery.delivered} of ${delivery.delivered + delivery.failed} settled`}
                />
                <Metric label="Failed" value={delivery.failed} color="red" />
                <Metric label="Awaiting" value={delivery.awaiting} color="blue" hint="Not yet confirmed" />
              </div>
              {Object.keys(notifications.byChannel || {}).length > 0 && (
                <div className="mt-3 pt-3 border-t border-secondary-100 flex flex-wrap gap-1.5">
                  {Object.entries(notifications.byChannel).map(([ch, c]) => {
                    const failed = Number(c.failed) || 0;
                    return (
                      <span
                        key={ch}
                        className={`text-[11px] px-2 py-0.5 rounded-full ${failed > 0
                          ? 'bg-red-50 text-red-700 font-medium'
                          : 'bg-secondary-100 text-secondary-600'}`}
                      >
                        {titleCase(ch)} {c.delivered}✓{failed > 0 ? ` ${failed}✗` : ''}
                        {c.sent > 0 ? ` · ${c.sent} pending` : ''}
                      </span>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <EmptyNote>No notifications sent in the last 7 days.</EmptyNote>
          )}
        </Panel>

        <Panel title="OTA rollouts (30d)" to="/firmware" linkLabel="Firmware">
          {otaTotal > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Succeeded" value={ota.success || 0} color="green" />
                <Metric label="In progress" value={otaMoving} color="blue" />
                <Metric
                  label="Queued"
                  value={otaQueued}
                  color="amber"
                  hint={otaQueued > 0 && otaMoving === 0 && !ota.success ? 'Not started' : null}
                />
              </div>
              <SegmentBar
                className="mt-3 pt-3 border-t border-secondary-100"
                segments={[
                  { label: 'Succeeded', value: ota.success || 0, fill: 'bg-emerald-500' },
                  { label: 'In progress', value: otaMoving, fill: 'bg-blue-400' },
                  { label: 'Queued', value: otaQueued, fill: 'bg-amber-500' },
                  { label: 'Failed', value: ota.failed || 0, fill: 'bg-red-500' },
                  { label: 'Cancelled', value: ota.cancelled || 0, fill: 'bg-secondary-300' },
                ]}
              />
            </>
          ) : (
            <EmptyNote>No rollouts assigned in the last 30 days.</EmptyNote>
          )}
        </Panel>
      </div>

      {/* Firmware spread + open alert types. With no open alerts there is no
          type breakdown to show, so firmware takes the full row rather than
          sitting beside an empty box. */}
      <div className={`grid grid-cols-1 gap-3 sm:gap-4 ${alerts.byType?.length > 0 ? 'lg:grid-cols-2' : ''}`}>
        <Panel title="Firmware across the fleet" to="/firmware" linkLabel="Firmware">
          {fw.rows.length === 0 ? (
            <EmptyNote>No devices.</EmptyNote>
          ) : (
            <>
              <div className="overflow-x-auto">
                {/* Capped so the columns stay together when this panel spans the
                    full row (i.e. when there are no open alerts beside it). */}
                <table className="w-full max-w-xl text-sm">
                  <thead className="text-secondary-500 text-xs">
                    <tr>
                      <th className="text-left font-medium py-1">Version</th>
                      <th className="text-left font-medium py-1">Board</th>
                      <th className="text-right font-medium py-1">Devices</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {fw.rows.map((f, i) => (
                      <tr key={`${f.version}-${f.board}-${i}`}>
                        <td className="py-1.5">
                          <span className={`font-mono ${f.behind ? 'text-amber-700' : 'text-secondary-700'}`}>
                            {f.version}
                          </span>
                          {f.isLatest && (
                            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-700">
                              NEWEST
                            </span>
                          )}
                          {f.behind && (
                            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              BEHIND
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 text-secondary-500">{(f.board || 'v3').toUpperCase()}</td>
                        <td className="py-1.5 text-right font-semibold text-secondary-900">{f.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {fw.laggingCount > 0 && (
                <p className="text-[11px] text-secondary-500 mt-3 pt-3 border-t border-secondary-100">
                  {fw.laggingCount} of {fw.rows.reduce((s, r) => s + r.count, 0)} devices are not on {fw.latest}.
                </p>
              )}
            </>
          )}
        </Panel>

        {alerts.byType?.length > 0 && (
          <Panel title="Open alerts by type" to="/alerts" linkLabel="Alerts">
            <ul className="divide-y divide-secondary-100">
              {alerts.byType.map((t) => (
                <li key={t.type} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-secondary-700">{titleCase(t.type)}</span>
                  <span className="font-semibold text-secondary-900">{t.count}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>

      {/* Check-in behaviour — aggregate only; scales to any number of patients
          and avoids surfacing individual check-in detail. The headline rate
          lives in the card above, so this section covers only what that card
          cannot say: quality and timing. */}
      <div className="bg-white rounded-xl shadow-card ring-1 ring-secondary-200/70 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-secondary-900">Check-in behaviour — today</h2>
            <p className="text-xs text-secondary-500 mt-0.5">How and when people are checking in</p>
          </div>
          <Link to="/reports" className="text-xs font-medium text-primary-600 hover:text-primary-700">Reports</Link>
        </div>

        {checkins.completedToday === 0 ? (
          <EmptyNote>
            No completed check-ins yet today — this fills in as presses arrive.
          </EmptyNote>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <Metric
                label="On time (in window)"
                value={checkins.onTimeRate != null ? `${checkins.onTimeRate}%` : '--'}
                color="blue"
                hint={`${checkins.onTime} of ${checkins.completedToday}`}
              />
              <Metric
                label="Medicine taken"
                value={checkins.medicineTakenRate != null ? `${checkins.medicineTakenRate}%` : '--'}
                color="green"
                hint={`${checkins.medicineTakenToday} of ${checkins.completedToday}`}
              />
              <Metric label="Manual check-ins" value={checkins.manual} color="amber" hint="Not a device press" />
              <Metric
                label="Avg time into window"
                value={checkins.avgOffsetMin != null ? `${checkins.avgOffsetMin} min` : '--'}
                color="secondary"
                hint={checkins.avgWindowMin != null ? `of a ${checkins.avgWindowMin}-min window` : null}
              />
            </div>

            {/* Where in the allowed window people check in (front vs back) */}
            <div className="border-t border-secondary-100 pt-4">
              <p className="text-sm font-medium text-secondary-700 mb-2">Where in the window they check in</p>
              {wp.positioned > 0 ? (
                <>
                  <div className="flex h-3 rounded-full overflow-hidden bg-secondary-100">
                    <div className="bg-emerald-500" style={{ width: `${(wp.front / wp.positioned) * 100}%` }} title={`Early: ${wp.front}`} />
                    <div className="bg-blue-400" style={{ width: `${(wp.middle / wp.positioned) * 100}%` }} title={`Middle: ${wp.middle}`} />
                    <div className="bg-amber-500" style={{ width: `${(wp.back / wp.positioned) * 100}%` }} title={`Late: ${wp.back}`} />
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-secondary-500">
                    <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1 align-middle" />Early (front third) {pct(wp.front, wp.positioned)}</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-blue-400 mr-1 align-middle" />Middle {pct(wp.middle, wp.positioned)}</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1 align-middle" />Late (back third) {pct(wp.back, wp.positioned)}</span>
                  </div>
                </>
              ) : (
                <EmptyNote>No window-position data yet today — captured as new check-ins arrive.</EmptyNote>
              )}
            </div>

            {/* Volume by scheduled slot */}
            {checkins.bySlot?.length > 0 && (
              <div className="border-t border-secondary-100 pt-4 mt-4">
                <p className="text-sm font-medium text-secondary-700 mb-2">By scheduled slot</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-secondary-500 text-xs">
                      <tr>
                        <th className="text-left font-medium py-1">Slot</th>
                        <th className="text-right font-medium py-1">Completed</th>
                        <th className="text-right font-medium py-1">Missed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {checkins.bySlot.map((s) => (
                        <tr key={s.slot ?? 'none'}>
                          <td className="py-1.5 text-secondary-700">{s.slot ? `Slot ${s.slot}` : 'Unscheduled'}</td>
                          <td className={`py-1.5 text-right font-semibold ${s.completed > 0 ? 'text-emerald-600' : 'text-secondary-300'}`}>{s.completed}</td>
                          <td className={`py-1.5 text-right font-semibold ${s.missed > 0 ? 'text-red-600' : 'text-secondary-300'}`}>{s.missed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const wsConnected = useWSConnected();

  const fetchStats = useCallback(() => api.get('/dashboard/stats')
    .then((r) => { setStats(r.data.stats); setUpdatedAt(new Date()); })
    .catch(() => {})
    .finally(() => setLoading(false)), []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useWS(LIVE_EVENTS, useCallback(() => { fetchStats(); }, [fetchStats]));

  if (loading) return <div className="flex items-center justify-center h-64 text-secondary-400">Loading...</div>;
  if (!stats) return <div className="text-red-500">Failed to load dashboard</div>;

  return <DashboardView stats={stats} updatedAt={updatedAt} wsConnected={wsConnected} />;
}
